import { useId, useState, useMemo, useEffect, useRef } from "react";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import { Controller, useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";
import Reload from "./icons/Reload";
import classnames from "classnames";

export interface OptionType {
  value: number | string;
  label: string;
}

export interface AdditionalOptions {
  page: number;
}

interface DataFetchProps<T> {
  data: T[];
  optionMapper: (item: T) => OptionType;
}

interface AsyncStatusProps {
  isDisabled?: boolean;
  isLoading?: boolean;
  isError?: boolean;
  error?: string | null;
  refetch?: () => void;
  selectedCountry?: number;
}

interface Props<T> extends DataFetchProps<T>, AsyncStatusProps {
  name: string;
  label?: string;
  loadOptions: LoadOptions<any, any>;
  placeholder?: string;
  additional?: AdditionalOptions;
  initialResolvedOption?: OptionType | null;
}

const BaseAsyncPaginate = <T,>({
  name,
  label,
  loadOptions,
  placeholder,
  additional,
  data,
  optionMapper,
  isLoading,
  isDisabled,
  isError,
  error,
  refetch,
  selectedCountry,
  initialResolvedOption,
}: Props<T>) => {
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext();
  const id = useId();
  const intl = useIntl();
  const previousCountry = useRef<number | undefined>(undefined);
  const isFirstRender = useRef(true);

  const formError = errors[name]?.message as string | undefined;

  const currentPlaceholder = isLoading
    ? intl.formatMessage({ id: "customComponents.loading" })
    : placeholder;

  const [extraOption, setExtraOption] = useState<OptionType | null>(null);

  // Combine the “base” options coming from the BE (fetched from `data`) with any “extra” (the selected input by the user) or “initial”(coming from the BE) options
  const mergedOptions = useMemo(() => {
    const baseMapped = (data ?? []).map(optionMapper);

    //Check if there is an "initial" (coming from the BE) value and it is not already included in the base options
    const includeInitial =
      initialResolvedOption !== null &&
      initialResolvedOption !== undefined &&
      initialResolvedOption.value !== null &&
      initialResolvedOption.value !== undefined &&
      baseMapped.every((o) => o.value !== initialResolvedOption.value);

    //Check if there is an "extra" (the selected input by the user) value and it is not already included in the base options or as "initial" value
    const includeExtra =
      extraOption !== null &&
      extraOption !== undefined &&
      extraOption.value !== null &&
      extraOption.value !== undefined &&
      (includeInitial
        ? [initialResolvedOption, ...baseMapped].every(
          (o) => o.value !== extraOption.value
        )
        : baseMapped.every((o) => o.value !== extraOption.value));

    const merged: OptionType[] = [];

    if (includeExtra) {
      //If the user has selected/typed something not in "baseMapped", we add it to the options so it appears first in the dropdown and can be shown as the chosen value
      merged.push(extraOption);
    }

    if (includeInitial) {
      //If there is an initial value, we add it to the options so it appears first in the dropdown and can be shown as the chosen value
      merged.push(initialResolvedOption);
    }

    // Finally, we add the base options
    merged.push(...baseMapped);

    return merged;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, optionMapper, initialResolvedOption, extraOption, selectedCountry]);

  const getSelectedOption = (
    fieldValue: number | string | undefined | null
  ): OptionType | null => {
    if (fieldValue !== undefined && fieldValue !== null) {
      return (
        mergedOptions.find((option) => option.value === fieldValue) || null
      );
    }
    return null;
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      previousCountry.current = selectedCountry;
      return;
    }

    if (previousCountry.current !== selectedCountry) {
      setExtraOption(null);
      if (name === 'state') {
        setValue(name, '');
      }
      previousCountry.current = selectedCountry;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry]);

  const handleChange = (
    option: OptionType | null,
    onChange: (value: string | number | null) => void
  ) => {
    onChange(option ? option.value : null);
    setExtraOption(option);
  };

  return (
    <div className="base-async-paginate">
      {label && (
        <label htmlFor={id} className="usadmin-base-select__label">
          {label}
        </label>
      )}
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          const selectedOptionObject = getSelectedOption(field.value);
          return (
            <AsyncPaginate
              inputId={id}
              {...field}
              isLoading={isLoading}
              value={selectedOptionObject}
              loadOptions={loadOptions}
              placeholder={currentPlaceholder}
              additional={additional}
              defaultOptions={true}
              // defaultOptions={mergedOptions}
              onChange={(option: OptionType | null) => {
                handleChange(option, field.onChange);
              }}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              isDisabled={isDisabled}
              cacheUniqs={[selectedCountry]}
            />
          );
        }}
      />
      {formError && <p className="usadmin-base-input__error">{formError}</p>}
      {!isDisabled && isError && (
        <div className="usadmin-base-select__error-message--wrapper">
          <span className="usadmin-base-input__error">{error}</span>
          <button
            type="button"
            onClick={() => refetch && refetch()}
            className="usadmin-base-select__refetch-button"
          >
            {intl.formatMessage({ id: "usAdmin.refetch" })}
            <Reload
              className={classnames(
                "usadmin-base-input__loading",
                isLoading && "usadmin__loading-animation"
              )}
            />
          </button>
        </div>
      )}
    </div>
  );
};

export default BaseAsyncPaginate;
